import { useCallback, useRef } from 'react';
import { useField } from 'formik';
import { PhoneInput } from '@kula-ai/components';
import { getTimezoneFromPhoneCode } from '@/utils/timezoneHelpers';
import './KPhoneField.css';

/**
 * FormBuilder wrapper for `PhoneInput` from `@kula-ai/components`.
 *
 * Value shape (PhoneInputValue):
 *   { countryCode: string, dialCode: string, number: string }
 *   - countryCode : ISO 3166-1 alpha-2 (e.g. "US", "GB")
 *   - dialCode    : country dial code including "+" (e.g. "+1", "+44")
 *   - number      : the local phone number entered by the user
 *
 * Config options (passed via `item` in FormBuilder config):
 *   - name              : Formik field name (required)
 *   - placeholder       : input placeholder text
 *   - defaultCountry    : ISO alpha-2 code for initial selection
 *   - preferredCountries: string[] of ISO codes shown at top of dropdown
 *   - excludeCountries  : string[] of ISO codes to hide from dropdown
 *   - size         : "sm" | "md" (default: "md")
 *   - disabled          : boolean
 *   - syncTimezone      : boolean — when true, auto-sets the `time_zone` Formik field
 *                         whenever the dial code changes to an unambiguous country
 *                         (e.g. +91 → "Asia/Kolkata"). Ambiguous codes (+1, +7…) are
 *                         ignored so the existing timezone is preserved.
 *
 * Validation: handled by the Yup schema in FormBuilder (type: 'phone').
 * Uses `validatePhoneNumber` from @kula-ai/components internally for
 * format validation — checks required + valid number for the country.
 *
 * Usage in FormBuilder config:
 *   {
 *     name: 'phone_number',
 *     label: 'Phone number',
 *     type: 'phone',
 *     isRequired: true,
 *     defaultCountry: 'US',
 *     preferredCountries: ['US', 'GB', 'IN'],
 *   }
 */
export const KPhoneField = ({ item, field, formik }) => {
  const {
    name,
    placeholder = 'Enter phone number',
    defaultCountry = undefined,
    preferredCountries,
    excludeCountries,
    size = 'md',
    disabled = false,
    countrySelectorTriggerClassName,
    syncTimezone = false,
  } = item;

  const [, meta] = useField(name);
  const { error, touched } = meta;

  const prevDialCodeRef = useRef(undefined);

  const handleChange = useCallback(
    (value) => {
      formik.setFieldValue(name, value);

      if (syncTimezone && value?.dialCode !== prevDialCodeRef.current) {
        prevDialCodeRef.current = value?.dialCode;
        const tz = getTimezoneFromPhoneCode(value?.dialCode);
        if (tz) {
          formik.setFieldValue('time_zone', tz);
        }
      }
    },
    [formik, name, syncTimezone]
  );

  return (
    <div className="k-phone-field-wrapper">
      <PhoneInput
        value={field.value || undefined}
        onChange={handleChange}
        defaultCountry={defaultCountry}
        placeholder={placeholder}
        size={size}
        disabled={disabled}
        preferredCountries={preferredCountries}
        excludeCountries={excludeCountries}
        error={error && touched}
        name={name}
        id={name}
        countrySelectorTriggerClassName={countrySelectorTriggerClassName}
      />
    </div>
  );
};
